import { LanguageT } from "../../context/GetLifeContext.context";
import { DOMAIN } from "../../constants/Global";
import { PrivacyPolicyLink } from "../../utils/InternationlChanges";
import { buildConsentEntry, ConsentEntry } from "../../utils/consentCapture";

export type TranslateFn = (key: string) => string;

/**
 * Claves i18n de la captura de consentimientos. Las traducciones viven en el
 * repositorio `getlife/translations` (se copian en build): mientras no lleguen,
 * `translateOr` cae al texto de `CONSENT_FALLBACKS` (ver docs/i18n-pending.md).
 */
export const CONSENT_KEYS = {
  title: "consent.healthData.title",
  text: "consent.healthData.text",
  policyLink: "consent.healthData.policyLink",
  continue: "consent.healthData.continue",
  marketing: "calculatorPage.acceptMarketing",
} as const;

type FallbackLocale = "es" | "fr" | "it" | "en";

interface ConsentFallbackTexts {
  title: string;
  text: string;
  policyLink: string;
  continue: string;
  marketing: string;
}

export const CONSENT_FALLBACKS: Record<FallbackLocale, ConsentFallbackTexts> = {
  es: {
    title: "Tratamiento de tus datos de salud",
    text: "Consiento que Life5 y la aseguradora traten mis datos de salud para evaluar el riesgo y tarificar mi seguro. Puedo retirar el consentimiento en cualquier momento escribiendo a dpo@life5.com.",
    policyLink: "Ver política de privacidad",
    continue: "Continuar",
    marketing:
      "Quiero recibir comunicaciones comerciales de Life5 sobre sus productos y servicios. Puedo retirar este consentimiento en cualquier momento escribiendo a dpo@life5.com.",
  },
  fr: {
    title: "Traitement de vos données de santé",
    text: "Je consens à ce que Life5 et l'assureur traitent mes données de santé afin d'évaluer le risque et de tarifer mon assurance. Je peux retirer mon consentement à tout moment en écrivant à dpo@life5.com.",
    policyLink: "Voir la politique de confidentialité",
    continue: "Continuer",
    marketing:
      "J'accepte de recevoir des communications commerciales de Life5 sur ses produits et services. Je peux retirer ce consentement à tout moment en écrivant à dpo@life5.com.",
  },
  it: {
    title: "Trattamento dei tuoi dati sulla salute",
    text: "Acconsento a che Life5 e la compagnia assicurativa trattino i miei dati sulla salute per valutare il rischio e quotare la mia polizza. Posso revocare il consenso in qualsiasi momento scrivendo a dpo@life5.com.",
    policyLink: "Leggi l'informativa sulla privacy",
    continue: "Continua",
    marketing:
      "Acconsento a ricevere comunicazioni commerciali da Life5 sui suoi prodotti e servizi. Posso revocare questo consenso in qualsiasi momento scrivendo a dpo@life5.com.",
  },
  en: {
    title: "Processing of your health data",
    text: "I consent to Life5 and the insurer processing my health data to assess the risk and price my insurance. I can withdraw my consent at any time by writing to dpo@life5.com.",
    policyLink: "See privacy policy",
    continue: "Continue",
    marketing:
      "I would like to receive commercial communications from Life5 about its products and services. I can withdraw this consent at any time by writing to dpo@life5.com.",
  },
};

export const fallbackLocale = (domain: string | undefined = DOMAIN): FallbackLocale => {
  if ("fr" === domain || "it" === domain || "en" === domain) return domain;
  if ("pt" === domain) return "en";
  return "es";
};

/** `translate()` devuelve la propia clave cuando falta la traducción. */
export const translateOr = (translate: TranslateFn, key: string, fallback: string): string => {
  const value = translate(key);
  return value === key || !value ? fallback : value;
};

export const consentText = (
  translate: TranslateFn,
  field: keyof ConsentFallbackTexts,
  domain: string | undefined = DOMAIN
): string => translateOr(translate, CONSENT_KEYS[field], CONSENT_FALLBACKS[fallbackLocale(domain)][field]);

export const privacyPolicyUrl = (domain: string | undefined = DOMAIN): string =>
  PrivacyPolicyLink[(domain as LanguageT) || "es"] || PrivacyPolicyLink.es;

/**
 * Texto de la casilla de marketing del calculador. En IT ya existe como
 * segunda casilla (opcional); en el resto de mercados es la tercera casilla.
 */
export const marketingConsentText = (
  translate: TranslateFn,
  domain: string | undefined = DOMAIN
): string =>
  "it" === domain
    ? translate("calculatorPage.acceptDataProcessing2")
    : consentText(translate, "marketing", domain);

/**
 * Consentimientos otorgados en la pantalla de contacto del calculador
 * (política de privacidad obligatoria + marketing opcional). El lead aún no
 * tiene token en ese momento, así que se dejan pendientes hasta tenerlo.
 */
export const buildCalculatorConsents = async (
  translate: TranslateFn,
  marketingGranted: boolean,
  domain: string | undefined = DOMAIN
): Promise<ConsentEntry[]> => [
  await buildConsentEntry("privacy_policy", true, translate("calculatorPage.acceptDataProcessing")),
  await buildConsentEntry("marketing", marketingGranted, marketingConsentText(translate, domain)),
];
